import { isRecord } from '@apps/web/components/phoebe_chat/record_utils';
import {
    formatToolFieldLabel,
    getOptionalString,
    ToolToken,
    ToolTopicSection,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';

export const SKILL_CONTEXT_TOOL_NAMES = new Set(['view_skills', 'load_skill']);

type SkillSummary = {
    readonly name: string;
    readonly displayName: string | null;
    readonly description: string | null;
    readonly loaded: boolean;
    readonly requiresUserConfirmation: boolean;
    readonly variant: string | null;
};

export function getSkillContextToolTitle({
    toolName,
    isComplete,
    args,
    output,
}: {
    readonly toolName: string;
    readonly isComplete: boolean;
    readonly args?: Record<string, unknown> | Array<unknown> | null;
    readonly output?: unknown;
}): string | null {
    if (toolName === 'view_skills') {
        return isComplete ? 'Checked available skills' : 'Checking skills';
    }

    if (toolName === 'load_skill') {
        const displayName = getLoadedSkillDisplayName({ args, output });
        if (!displayName) {
            return isComplete ? 'Loaded skill' : 'Loading skill';
        }
        const titleName = formatSkillNameForTitle(displayName);
        return isComplete
            ? `Loaded ${titleName} skill`
            : `Loading ${titleName} skill`;
    }

    return null;
}

export function getSkillContextHeaderDetail({
    toolName,
    args,
    output,
}: {
    readonly toolName: string;
    readonly args: Record<string, unknown> | Array<unknown> | null;
    readonly output: unknown;
}): string | null {
    if (toolName === 'view_skills') {
        const skills = getSkillSummaries(output);
        if (skills.length === 0) {
            return 'No skills returned';
        }
        const loadedCount = skills.filter((skill) => skill.loaded).length;
        const parts = [`${skills.length} available`];
        if (loadedCount > 0) {
            parts.push(`${loadedCount} loaded`);
        }
        return parts.join(', ');
    }

    if (toolName === 'load_skill') {
        const displayName = getLoadedSkillDisplayName({ args, output });
        return displayName ? `${displayName} context` : 'Skill context';
    }

    return null;
}

export function SkillContextToolCard({
    toolName,
    output,
    args,
}: {
    readonly toolName: string;
    readonly output: unknown;
    readonly args?: Record<string, unknown> | Array<unknown> | null;
}) {
    if (toolName === 'view_skills') {
        return <ViewSkillsCard skills={getSkillSummaries(output)} />;
    }

    if (toolName === 'load_skill') {
        return <LoadedSkillCard args={args} output={output} />;
    }

    return null;
}

function ViewSkillsCard({ skills }: { readonly skills: Array<SkillSummary> }) {
    const loadedCount = skills.filter((skill) => skill.loaded).length;
    const approvalCount = skills.filter(
        (skill) => skill.requiresUserConfirmation,
    ).length;
    const visibleSkills = skills.slice(0, 5);

    return (
        <div className="space-y-2">
            <div className="border-border/60 bg-muted/30 rounded-lg border px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-foreground text-sm font-semibold">
                        Skill context
                    </p>
                    {skills.length > 0 ? (
                        <div className="flex flex-wrap justify-end gap-1">
                            <ToolToken tone="blue">
                                {skills.length} available
                            </ToolToken>
                            {loadedCount > 0 ? (
                                <ToolToken tone="green">
                                    {loadedCount} loaded
                                </ToolToken>
                            ) : null}
                            {approvalCount > 0 ? (
                                <ToolToken>{approvalCount} approval</ToolToken>
                            ) : null}
                        </div>
                    ) : null}
                </div>
                {skills.length === 0 ? (
                    <p className="text-muted-foreground mt-1 text-xs">
                        No skills returned
                    </p>
                ) : null}
            </div>
            {visibleSkills.length > 0 ? (
                <ToolTopicSection title="Available skills">
                    <div className="divide-border/60 divide-y">
                        {visibleSkills.map((skill) => (
                            <SkillSummaryRow key={skill.name} skill={skill} />
                        ))}
                        {skills.length > visibleSkills.length ? (
                            <p className="text-muted-foreground pt-2 text-xs">
                                +{skills.length - visibleSkills.length} more
                                skills
                            </p>
                        ) : null}
                    </div>
                </ToolTopicSection>
            ) : null}
        </div>
    );
}

function SkillSummaryRow({ skill }: { readonly skill: SkillSummary }) {
    return (
        <div className="py-2 first:pt-0 last:pb-0">
            <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-foreground text-xs font-semibold">
                    {skill.displayName ?? formatSkillDisplayName(skill.name)}
                </span>
                {skill.loaded ? (
                    <ToolToken tone="green">Loaded</ToolToken>
                ) : null}
                {skill.variant === 'early_access' ? (
                    <ToolToken tone="green">Early access</ToolToken>
                ) : null}
                {skill.requiresUserConfirmation ? (
                    <ToolToken>Approval</ToolToken>
                ) : null}
            </div>
            {skill.description ? (
                <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                    {skill.description}
                </p>
            ) : null}
        </div>
    );
}

function LoadedSkillCard({
    args,
    output,
}: {
    readonly args?: Record<string, unknown> | Array<unknown> | null;
    readonly output: unknown;
}) {
    const displayName = getLoadedSkillDisplayName({ args, output });
    const variant = getSkillVariantFromOutput(output);
    return (
        <ToolTopicSection title="Loaded skill">
            <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-1.5">
                    <p className="text-foreground text-sm font-semibold">
                        {displayName ?? 'Skill'}
                    </p>
                    {variant === 'early_access' ? (
                        <ToolToken tone="green">Early access</ToolToken>
                    ) : null}
                </div>
                <p className="text-muted-foreground text-xs leading-relaxed">
                    Instruction context is available for this run.
                </p>
            </div>
        </ToolTopicSection>
    );
}

function getSkillSummaries(output: unknown): Array<SkillSummary> {
    const rawSkills = getRawSkillList(output);
    return rawSkills
        .map((skill) => {
            if (!isRecord(skill)) {
                return null;
            }
            const name =
                getOptionalString(skill['name']) ??
                getOptionalString(skill['skill_name']);
            if (!name) {
                return null;
            }
            return {
                name,
                displayName: getOptionalString(skill['skill_display_name']),
                description: getOptionalString(skill['description']),
                loaded:
                    skill['loaded'] === true ||
                    skill['is_loaded'] === true ||
                    skill['status'] === 'loaded',
                requiresUserConfirmation:
                    skill['requires_user_confirmation'] === true ||
                    skill['requiresUserConfirmation'] === true,
                variant: getOptionalString(skill['skill_variant']),
            };
        })
        .filter((skill): skill is SkillSummary => skill !== null);
}

function getRawSkillList(output: unknown): Array<unknown> {
    if (Array.isArray(output)) {
        return output;
    }
    if (!isRecord(output)) {
        return [];
    }
    if (Array.isArray(output['skills'])) {
        return output['skills'];
    }
    if (Array.isArray(output['available_skills'])) {
        return output['available_skills'];
    }
    return [];
}

function getRequestedSkillName(
    args: Record<string, unknown> | Array<unknown> | null | undefined,
): string | null {
    if (!isRecord(args)) {
        return null;
    }
    return (
        getOptionalString(args['name']) ??
        getOptionalString(args['skill_name']) ??
        getOptionalString(args['skill'])
    );
}

function getLoadedSkillDisplayName({
    args,
    output,
}: {
    readonly args?: Record<string, unknown> | Array<unknown> | null;
    readonly output?: unknown;
}): string | null {
    const displayName = getSkillDisplayNameFromOutput(output);
    if (displayName) {
        return displayName;
    }

    const skillName =
        getSkillNameFromOutput(output) ?? getRequestedSkillName(args);
    return skillName ? formatSkillDisplayName(skillName) : null;
}

function getSkillDisplayNameFromOutput(output: unknown): string | null {
    if (!isRecord(output)) {
        return null;
    }
    return (
        getOptionalString(output['skill_display_name']) ??
        getOptionalString(output['display_name'])
    );
}

function getSkillNameFromOutput(output: unknown): string | null {
    if (!isRecord(output)) {
        return null;
    }
    return (
        getOptionalString(output['skill_name']) ??
        getOptionalString(output['name'])
    );
}

function getSkillVariantFromOutput(output: unknown): string | null {
    if (!isRecord(output)) {
        return null;
    }
    return getOptionalString(output['skill_variant']);
}

function formatSkillDisplayName(skillName: string): string {
    return formatToolFieldLabel(skillName).replace(/\s+/g, ' ').trim();
}

function formatSkillNameForTitle(skillNameOrDisplayName: string): string {
    if (looksLikeHumanReadableSkillDisplayName(skillNameOrDisplayName)) {
        return skillNameOrDisplayName.replace(/\s+/g, ' ').trim();
    }
    return formatSkillDisplayName(skillNameOrDisplayName).toLowerCase();
}

function looksLikeHumanReadableSkillDisplayName(value: string): boolean {
    const normalizedValue = value.trim();
    return (
        normalizedValue.includes(' ') &&
        !normalizedValue.includes('_') &&
        /[A-Z]/u.test(normalizedValue)
    );
}
